import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { collection, doc, setDoc, deleteDoc, onSnapshot } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { db } from '../firebase';

const STORAGE_KEY = 'oplixi_mylist';
const MyListContext = createContext();

function getStoredList() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveStoredList(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // Storage full or unavailable
  }
}

export function MyListProvider({ children }) {
  const { currentUser } = useAuth();
  const [myList, setMyList] = useState(getStoredList);

  // Sync from Firestore when logged in, localStorage when not
  useEffect(() => {
    if (!currentUser || !db || currentUser.isGuest) {
      // Not logged in or guest — use localStorage
      setMyList(getStoredList());
      return;
    }

    const colRef = collection(db, 'users', currentUser.uid, 'myList');
    const unsubscribe = onSnapshot(colRef, (snapshot) => {
      const items = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      items.sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0));
      setMyList(items);
      saveStoredList(items);
    }, () => {
      // Firestore failed — fall back to localStorage
      setMyList(getStoredList());
    });

    return unsubscribe;
  }, [currentUser]);

  const addToList = useCallback(async (movie) => {
    const movieId = String(movie.id);
    console.info(`[MyList] Adding movie "${movie.title || movie.name}" (${movieId}), logged in: ${!!currentUser}`);
    const item = {
      id: movieId,
      title: movie.title || movie.name || '',
      thumbnailUrl: movie.thumbnailUrl || movie.poster_path || '',
      publishedAt: movie.publishedAt || movie.release_date || movie.first_air_date || '',
      duration: movie.duration || movie.runtime || 0,
      addedAt: Date.now(),
    };

    // Optimistic update — always works
    setMyList((prev) => {
      if (prev.some((i) => i.id === movieId)) return prev;
      const updated = [item, ...prev];
      saveStoredList(updated);
      return updated;
    });

    // Persist to Firestore if logged in (not guest)
    if (currentUser && db && !currentUser.isGuest) {
      try {
        const { id: _, ...data } = item;
        const docRef = doc(db, 'users', currentUser.uid, 'myList', movieId);
        await setDoc(docRef, data);
      } catch (error) {
        console.warn('Firestore save failed, kept in localStorage:', error.message);
      }
    }
  }, [currentUser]);

  const removeFromList = useCallback(async (movieId) => {
    const id = String(movieId);

    setMyList((prev) => {
      const updated = prev.filter((i) => i.id !== id);
      saveStoredList(updated);
      return updated;
    });

    if (currentUser && db && !currentUser.isGuest) {
      try {
        const docRef = doc(db, 'users', currentUser.uid, 'myList', id);
        await deleteDoc(docRef);
      } catch (error) {
        console.warn('Firestore delete failed:', error.message);
      }
    }
  }, [currentUser]);

  const isInList = useCallback((movieId) => {
    return myList.some((item) => item.id === String(movieId));
  }, [myList]);

  return (
    <MyListContext.Provider value={{ myList, addToList, removeFromList, isInList }}>
      {children}
    </MyListContext.Provider>
  );
}

export function useMyList() {
  const context = useContext(MyListContext);
  if (!context) {
    throw new Error('useMyList must be used within a MyListProvider');
  }
  return context;
}
